import ky, { HTTPError } from 'ky';

import { SERVER_URL } from 'src/config';
import { refreshToken as refreshCustomToken } from './msal.service';
import i18n from 'src/i18n';
import { supportModeService } from 'src/services/supportMode.service';

const TOKEN_STORAGE_KEY = 'mundu-auth-tokens';
const REFRESH_THRESHOLD_MS = 60 * 1000; // refresh 60s before expiry

type AuthTokens = {
    accessToken: string;
    refreshToken: string;
    tokenEndpoint: string;
};

type StoredAuthState = AuthTokens & {
    expiresAt: number;
    claims?: Record<string, unknown>;
    showOnboarding?: boolean;
};

export class AuthError extends Error {
    status?: number;
    code?: string;
    /** Response details, such as the new code session sent with INVITATION_CODE_RESENT. */
    details?: Record<string, unknown>;

    constructor(
        message: string,
        status?: number,
        code?: string,
        details?: Record<string, unknown>,
    ) {
        super(message);
        this.name = 'AuthError';
        this.status = status;
        this.code = code;
        this.details = details;
    }
}

/** Error from the account-access endpoints, with the stable code the UI translates. */
export class AccountAccessError extends Error {
    constructor(
        message: string,
        readonly status: number,
        readonly code?: string,
        readonly retryAfter?: number,
    ) {
        super(message);
        this.name = 'AccountAccessError';
    }
}

const decodeTokenPayload = (
    token?: string,
): Record<string, unknown> | undefined => {
    if (!token) return undefined;
    const parts = token.split('.');
    if (parts.length < 2) return undefined;
    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(
            base64.length + ((4 - (base64.length % 4)) % 4),
            '=',
        );
        const decoded = atob(padded);
        return JSON.parse(decoded);
    } catch (error) {
        console.warn('Failed to decode token payload', error);
        return undefined;
    }
};

const getExpiresAt = (claims?: Record<string, unknown>) => {
    const exp = typeof claims?.exp === 'number' ? claims.exp : undefined;
    if (typeof exp === 'number') {
        return exp * 1000;
    }
    return Date.now() + 60 * 60 * 1000; // default to one hour
};

const extractShowOnboarding = (claims?: Record<string, unknown>) => {
    if (!claims) return false;
    if (typeof claims.showOnboarding === 'boolean') {
        return claims.showOnboarding;
    }

    const firstSignInKey = Object.keys(claims).find(key =>
        key.toLowerCase().includes('firstsignin'),
    );

    if (firstSignInKey) {
        const value = claims[firstSignInKey];
        if (typeof value === 'boolean') {
            return value;
        }
    }

    return false;
};

export class AuthService {
    public error: Error | null;
    private tokens?: StoredAuthState;
    private readonly ropcClientId: string;
    private refreshPromise?: Promise<void>;

    constructor() {
        this.error = null;
        this.ropcClientId = process.env.REACT_APP_AD_B2C_CLIENT_ID || '';
        this.tokens = this.loadTokens();
    }

    private loadTokens(): StoredAuthState | undefined {
        const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
        if (!stored) return undefined;
        try {
            const parsed = JSON.parse(stored) as StoredAuthState;
            return parsed;
        } catch (error) {
            console.warn('Failed to parse stored auth tokens', error);
            sessionStorage.removeItem(TOKEN_STORAGE_KEY);
            return undefined;
        }
    }

    private persistTokens(state?: StoredAuthState) {
        if (!state) {
            sessionStorage.removeItem(TOKEN_STORAGE_KEY);
            return;
        }
        sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(state));
    }

    private setTokens(tokens: AuthTokens) {
        const claims = decodeTokenPayload(tokens.accessToken);
        const showOnboarding = extractShowOnboarding(claims);
        this.tokens = {
            ...tokens,
            expiresAt: getExpiresAt(claims),
            claims,
            showOnboarding,
        };
        this.persistTokens(this.tokens);
    }

    private clearTokens() {
        this.tokens = undefined;
        this.persistTokens(undefined);
    }

    private async refreshAccessToken() {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = (async () => {
            if (!this.tokens) return;
            if (!this.tokens.refreshToken || !this.tokens.tokenEndpoint) {
                this.clearTokens();
                return;
            }
            if (!this.ropcClientId) {
                console.warn('Missing ROPC client id; cannot refresh token');
                this.clearTokens();
                return;
            }

            try {
                const { ok, status, data } = await refreshCustomToken({
                    tokenEndpoint: this.tokens.tokenEndpoint,
                    clientId: this.ropcClientId,
                    refreshToken: this.tokens.refreshToken,
                });

                if (!ok) {
                    throw new AuthError(i18n.t('errors.refreshFailed'), status);
                }

                if (!data.access_token) {
                    throw new AuthError(i18n.t('errors.missingAccessToken'));
                }

                this.setTokens({
                    accessToken: data.access_token,
                    refreshToken:
                        data.refresh_token || this.tokens.refreshToken,
                    tokenEndpoint: this.tokens.tokenEndpoint,
                });
                this.error = null;
            } catch (error) {
                this.error =
                    error instanceof Error
                        ? error
                        : new Error(i18n.t('errors.unknownRefresh'));
                this.clearTokens();
                throw this.error;
            }
        })();

        try {
            await this.refreshPromise;
        } finally {
            this.refreshPromise = undefined;
        }
    }

    isAuthenticated() {
        if (!this.tokens) return false;
        if (this.tokens.expiresAt <= Date.now()) {
            return false;
        }
        return true;
    }

    getActiveAccount() {
        if (!this.tokens) return undefined;
        return {
            idTokenClaims: {
                ...this.tokens.claims,
                showOnboarding: this.tokens.showOnboarding,
            },
        };
    }

    async loginWithCredentials(
        credentials: {
            email?: string;
            username?: string;
            phoneNumber?: string;
            password: string;
        },
        newPassword?: string,
    ) {
        try {
            this.error = null;
            const payload = newPassword
                ? { ...credentials, newPassword }
                : credentials;
            const data = await ky
                .post('auth/login', {
                    prefixUrl: SERVER_URL,
                    json: payload,
                    timeout: 30_000,
                })
                .json<AuthTokens>();

            this.setTokens(data);
            this.error = null;
        } catch (error) {
            if (error instanceof HTTPError) {
                const status = error.response?.status;
                let serverCode: string | undefined;
                let serverDetails: Record<string, unknown> | undefined;
                let message = i18n.t('errors.loginFailed');

                if (error.response) {
                    try {
                        const details = await error.response.json();
                        if (details && typeof details === 'object') {
                            serverDetails = details;
                            if (typeof details.code === 'string') {
                                serverCode = details.code;
                            } else if (
                                details.message &&
                                typeof details.message === 'object' &&
                                typeof details.message.code === 'string'
                            ) {
                                serverCode = details.message.code;
                            }

                            if (typeof details.message === 'string') {
                                message = details.message;
                            } else if (
                                details.message &&
                                typeof details.message === 'object' &&
                                typeof details.message.message === 'string'
                            ) {
                                message = details.message.message;
                            }
                        }
                    } catch (parseError) {
                        console.warn(
                            'Failed to parse auth error response',
                            parseError,
                        );
                    }
                }

                if (serverCode === 'PASSWORD_CHANGE_REQUIRED') {
                    message = i18n.t('errors.passwordChangeRequired');
                } else if (status === 403) {
                    message = i18n.t('errors.invalidCredentials');
                } else if (status === 400) {
                    message = i18n.t('errors.accountNotActivated');
                }

                const authError = new AuthError(
                    message,
                    status,
                    serverCode,
                    serverDetails,
                );
                this.error = authError;
                throw authError;
            }

            this.error =
                error instanceof Error
                    ? error
                    : new Error(i18n.t('errors.unknownLogin'));
            throw this.error;
        }
    }

    async accountAccess<T>(
        path: string,
        payload?: unknown,
        authenticated = false,
    ): Promise<T> {
        const accessToken = authenticated
            ? await this.getUserToken()
            : undefined;
        const options = {
            prefixUrl: SERVER_URL,
            timeout: 30_000,
            headers: accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : undefined,
        };
        try {
            return await (payload === undefined
                ? ky.get(`auth/access/${path}`, options)
                : ky.post(`auth/access/${path}`, { ...options, json: payload })
            ).json<T>();
        } catch (error) {
            if (error instanceof HTTPError) {
                const data = await error.response.json().catch(() => ({}));
                throw new AccountAccessError(
                    typeof data.message === 'string'
                        ? data.message
                        : i18n.t('errors.unknownLogin'),
                    error.response.status,
                    data.code,
                    data.retryAfter,
                );
            }
            throw error;
        }
    }

    acceptAccessTokens(tokens: AuthTokens) {
        this.setTokens(tokens);
    }

    logout() {
        supportModeService.clear();
        this.clearTokens();
        this.error = null;
    }

    async getUserToken(): Promise<string | undefined> {
        if (!this.tokens) return undefined;
        const shouldRefresh =
            this.tokens.expiresAt - REFRESH_THRESHOLD_MS <= Date.now();
        if (shouldRefresh) {
            await this.refreshAccessToken().catch(() => undefined);
        }
        return this.tokens?.accessToken;
    }
}
